import React, { useEffect, useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useHighlightRow } from "../hooks/useHighlightRow";
import Layout from "../components/Layout";
import apiClient from "../services/api";
import EditSourceModal from "../components/grants/sources/EditSourceModal";
import AddManualArticleModal from "../components/grants/sources/AddManualArticleModal";
import MarkSourceCheckedModal from "../components/grants/sources/MarkSourceCheckedModal";
import SourcesFilters, { CompetitionCount, RegionCount } from "../components/grants/sources/SourcesFilters";
import { EmptyState } from "../components/common/EmptyState";
import { ErrorState } from "../components/common/ErrorState";
import { StatusBadge, StatusType } from "../components/common/StatusBadge";
import { RobotsTxtStatusCell } from "../components/grants/sources/RobotsTxtStatusCell";
import { formatDate } from "../utils/date-format";
import { useToast } from "../contexts/ToastContext";
import { describeCronPattern } from "../components/grants/sources/utils/cron-validation";
import { sanitizeErrorMessage } from "../utils/error-sanitizer";
import { RobotsTxtStatus } from "../constants/robots-txt-status";

export { RobotsTxtStatus };

const MANUAL_SOURCE_TYPE = "MANUAL" as const;

interface InformationSource {
  id: string;
  name: string;
  sourceType: string;
  url: string | null;
  status: string;
  checkFrequency: string;
  lastCheckedAt: string | null;
  lastSuccessAt: string | null;
  errorCount: number;
  lastError: string | null;
  articlesFoundTotal: number;
  articlesFoundLastCheck: number;
  isActive: boolean;
  robotsTxtStatus: RobotsTxtStatus;
  robotsTxtLastCheckedAt: string | null;
  project: {
    id: string;
    name: string;
    operator: {
      id: string;
      name: string;
      shortName: string | null;
    } | null;
    competition: {
      id: string;
      name: string;
      code: string | null;
    } | null;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface SourcesResponse {
  sources: InformationSource[];
  total: number;
  competitionCounts: CompetitionCount[];
  regionCounts: RegionCount[];
  robotsTxtStatusCounts?: Record<string, number>;
}

const sourceTypeLabels: Record<string, string> = {
  RSS: "RSS",
  WEBPAGE: "Strona WWW",
  SITEMAP: "Sitemap",
  WP_JSON: "WordPress API",
  STATIC_PAGE: "Pojedyncza strona",
  API: "API",
  MANUAL: "Ręczne",
};

/**
 * Maps source status to StatusBadge status type and display label.
 */
const getStatusBadgeProps = (
  status: string
): { status: StatusType; label: string } => {
  switch (status) {
    case "ACTIVE":
      return { status: "active", label: "Aktywne" };
    case "INACTIVE":
      return { status: "inactive", label: "Nieaktywne" };
    case "ERROR":
      return { status: "error", label: "Błąd" };
    default:
      return { status: "unknown", label: status };
  }
};

/**
 * Admin page for managing information sources.
 * CRUD operations for RSS feeds, web pages, and other data sources.
 */
interface DiscoveryResult {
  success: boolean;
  sourcesProcessed: number;
  articlesDiscovered: number;
  newArticles: number;
  errors: Array<{ sourceId: string; message: string }>;
  durationMs: number;
}

function pluralizeSourceCount(n: number): string {
  if (n === 1) return '1 źródło';
  if (n >= 2 && n <= 4) return `${n} źródła`;
  return `${n} źródeł`;
}

function buildDiscoveryToast(result: DiscoveryResult): { message: string; type: "success" | "warning" | "error"; duration: number } {
  const successCount = result.sourcesProcessed - result.errors.length;
  if (result.errors.length === 0) {
    return {
      message: `Discovery zakończone. Przetworzono ${result.sourcesProcessed} źródeł, znaleziono ${result.articlesDiscovered} artykułów (${result.newArticles} nowych)`,
      type: "success",
      duration: 5000,
    };
  } else if (successCount > 0) {
    return {
      message: `Przetworzono ${successCount}/${result.sourcesProcessed} źródeł. ${result.errors.length} błędów`,
      type: "warning",
      duration: 8000,
    };
  } else {
    return {
      message: `Błąd discovery: ${result.errors[0].message}`,
      type: "error",
      duration: 8000,
    };
  }
}

const GrantsSources: React.FC = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingSourceId, setEditingSourceId] = useState<string | null>(null);
  const [runningDiscoveryId, setRunningDiscoveryId] = useState<string | null>(
    null
  );
  const [isRetrying, setIsRetrying] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const { highlightedRowRef, highlightId } = useHighlightRow();

  // Manual source workflow modals
  const [addArticleSource, setAddArticleSource] = useState<InformationSource | null>(null);
  const [markCheckedSource, setMarkCheckedSource] = useState<InformationSource | null>(null);

  // Inline robots.txt check state: sourceId → pending | updated status
  const [robotsTxtCheckPending, setRobotsTxtCheckPending] = useState<Set<string>>(new Set());
  const [robotsTxtInlineStatus, setRobotsTxtInlineStatus] = useState<
    Map<string, { status: RobotsTxtStatus; lastCheckedAt: string | null }>
  >(new Map());

  // Get filter values from URL params
  const competitionId = searchParams.get("competitionId") || "";
  const region = searchParams.get("region") || "";
  const sourceType = searchParams.get("sourceType") || "";
  const neverChecked = searchParams.get("neverChecked") === "true";
  const robotsTxtStatus = searchParams.get("robotsTxtStatus") || "";
  const sortBy = searchParams.get("sortBy") || "";
  const sortOrder = searchParams.get("sortOrder") || "";
  const hasFiltersActive = !!(competitionId || region || sourceType || neverChecked || robotsTxtStatus);

  // Update URL params when filters change
  const handleCompetitionChange = useCallback(
    (value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set("competitionId", value);
      } else {
        newParams.delete("competitionId");
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleRegionChange = useCallback(
    (value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set("region", value);
      } else {
        newParams.delete("region");
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleSourceTypeChange = useCallback(
    (value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set("sourceType", value);
      } else {
        newParams.delete("sourceType");
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleNeverCheckedChange = useCallback(
    (checked: boolean) => {
      const newParams = new URLSearchParams(searchParams);
      if (checked) {
        newParams.set("neverChecked", "true");
      } else {
        newParams.delete("neverChecked");
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleRobotsTxtStatusChange = useCallback(
    (value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set("robotsTxtStatus", value);
      } else {
        newParams.delete("robotsTxtStatus");
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleClearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // 3-click sort cycle: ASC -> DESC -> reset
  const handleSortClick = useCallback(
    (column: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (sortBy === column) {
        if (sortOrder === "ASC") {
          newParams.set("sortOrder", "DESC");
        } else {
          // Reset sorting
          newParams.delete("sortBy");
          newParams.delete("sortOrder");
        }
      } else {
        newParams.set("sortBy", column);
        newParams.set("sortOrder", "ASC");
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams, sortBy, sortOrder]
  );

  useEffect(() => {
    document.title = "Źródła danych - KiedyNabory.pl";
  }, []);

  // Reset selection when filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [competitionId, region, sourceType, neverChecked, robotsTxtStatus]);

  // Build API URL with query params
  const buildApiUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (competitionId) params.set("competitionId", competitionId);
    if (region) params.set("region", region);
    if (sourceType) params.set("sourceType", sourceType);
    if (neverChecked) params.set("neverChecked", "true");
    if (robotsTxtStatus) params.set("robotsTxtStatus", robotsTxtStatus);
    if (sortBy) params.set("sortBy", sortBy);
    if (sortOrder) params.set("sortOrder", sortOrder);
    const queryString = params.toString();
    return `/grants/sources${queryString ? `?${queryString}` : ""}`;
  }, [competitionId, region, sourceType, neverChecked, robotsTxtStatus, sortBy, sortOrder]);

  const { data, isLoading, error, refetch } = useQuery<SourcesResponse>({
    queryKey: ["grants-sources", competitionId, region, sourceType, neverChecked, robotsTxtStatus, sortBy, sortOrder],
    queryFn: async () => {
      const { data } = await apiClient.get(buildApiUrl());
      return data;
    },
  });

  // Reset inline robots.txt status overrides when list data refreshes
  useEffect(() => {
    setRobotsTxtInlineStatus(new Map());
  }, [data]);

  // Prepare source type options for dropdown
  const sourceTypeOptions = Object.entries(sourceTypeLabels).map(
    ([value, label]) => ({ value, label })
  );

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/grants/sources/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-sources"] });
    },
  });

  const discoveryMutation = useMutation({
    mutationFn: async (sourceId: string) => {
      setRunningDiscoveryId(sourceId);
      const { data } = await apiClient.post<DiscoveryResult>(
        "/grants/admin/discovery/trigger",
        { sourceId }
      );
      return data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["grants-sources"] });
      addToast(buildDiscoveryToast(result));
    },
    onError: (error: Error) => {
      addToast({
        message: `Błąd: ${error.message}`,
        type: "error",
        duration: 8000,
      });
    },
    onSettled: () => {
      setRunningDiscoveryId(null);
    },
  });

  const bulkDiscoveryMutation = useMutation({
    mutationFn: async (sourceIds: string[]) => {
      const { data } = await apiClient.post<DiscoveryResult>(
        "/grants/admin/discovery/trigger",
        { sourceIds }
      );
      return data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["grants-sources"] });
      setSelectedIds(new Set());
      addToast(buildDiscoveryToast(result));
    },
    onError: (error: Error) => {
      // Keep selectedIds on error to allow easy retry
      addToast({
        message: `Błąd: ${error.message}`,
        type: "error",
        duration: 8000,
      });
    },
  });

  const handleRunDiscovery = (source: InformationSource) => {
    discoveryMutation.mutate(source.id);
  };

  const nonManualSources = data?.sources?.filter((s) => s.sourceType !== MANUAL_SOURCE_TYPE) ?? [];
  const allNonManualSelected =
    nonManualSources.length > 0 &&
    nonManualSources.every((s) => selectedIds.has(s.id));
  const someNonManualSelected =
    nonManualSources.some((s) => selectedIds.has(s.id)) && !allNonManualSelected;

  // Sync indeterminate state on header checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = someNonManualSelected;
    }
  }, [someNonManualSelected]);

  const handleSelectAll = () => {
    if (allNonManualSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(nonManualSources.map((s) => s.id)));
    }
  };

  const handleToggleSource = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDelete = (source: InformationSource) => {
    if (
      window.confirm(`Czy na pewno chcesz usunąć źródło "${source.name}"?`)
    ) {
      deleteMutation.mutate(source.id);
    }
  };

  const handleRetry = async () => {
    if (isRetrying) return;
    setIsRetrying(true);
    try {
      await refetch();
    } finally {
      setIsRetrying(false);
    }
  };

  const handleCheckRobotsTxt = async (sourceId: string) => {
    setRobotsTxtCheckPending((prev) => {
      const next = new Set(prev);
      next.add(sourceId);
      return next;
    });
    try {
      const { data: result } = await apiClient.post<{
        robotsTxtStatus: RobotsTxtStatus;
        robotsTxtLastCheckedAt: string;
      }>(`/grants/sources/${sourceId}/check-robots-txt`);
      setRobotsTxtInlineStatus((prev) => {
        const next = new Map(prev);
        next.set(sourceId, {
          status: result.robotsTxtStatus,
          lastCheckedAt: result.robotsTxtLastCheckedAt,
        });
        return next;
      });
    } catch (err: unknown) {
      const message = sanitizeErrorMessage(err as Parameters<typeof sanitizeErrorMessage>[0]);
      addToast({ message: `Błąd sprawdzania robots.txt: ${message}`, type: "error", duration: 5000 });
    } finally {
      setRobotsTxtCheckPending((prev) => {
        const next = new Set(prev);
        next.delete(sourceId);
        return next;
      });
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">
              Źródła informacji
            </h1>
            <p className="mt-1 text-sm text-neutral-600 text-left">
              Zarządzanie źródłami danych o naborach (RSS, strony WWW, API)
            </p>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Dodaj źródło
          </button>
        </div>

        {/* Filters */}
        <SourcesFilters
          competitionCounts={data?.competitionCounts || []}
          regionCounts={data?.regionCounts || []}
          sourceTypes={sourceTypeOptions}
          selectedCompetitionId={competitionId}
          selectedRegion={region}
          selectedSourceType={sourceType}
          neverChecked={neverChecked}
          robotsTxtStatusCounts={data?.robotsTxtStatusCounts}
          selectedRobotsTxtStatus={robotsTxtStatus}
          isLoading={isLoading}
          onCompetitionChange={handleCompetitionChange}
          onRegionChange={handleRegionChange}
          onSourceTypeChange={handleSourceTypeChange}
          onNeverCheckedChange={handleNeverCheckedChange}
          onRobotsTxtStatusChange={handleRobotsTxtStatusChange}
          onClearFilters={handleClearFilters}
        />

        {/* Bulk action toolbar */}
        {selectedIds.size > 0 && (
          <div
            className="flex items-center gap-4 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg"
            data-testid="bulk-discovery-toolbar"
          >
            <span
              className="text-sm text-primary-800 font-medium"
              aria-live="polite"
            >
              Zaznaczono: {pluralizeSourceCount(selectedIds.size)}
            </span>
            <button
              onClick={() => bulkDiscoveryMutation.mutate(Array.from(selectedIds))}
              disabled={bulkDiscoveryMutation.isPending || selectedIds.size > 50}
              className={`px-3 py-1.5 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                bulkDiscoveryMutation.isPending || selectedIds.size > 50
                  ? "bg-primary-300 text-white cursor-wait"
                  : "bg-primary-600 text-white hover:bg-primary-700"
              }`}
              data-testid="bulk-discovery-btn"
              aria-label={`Uruchom discovery dla ${selectedIds.size} zaznaczonych źródeł`}
            >
              {bulkDiscoveryMutation.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Przetwarzanie...
                </span>
              ) : (
                `Uruchom discovery (${selectedIds.size})`
              )}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-2 py-1 text-sm text-primary-700 hover:text-primary-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 rounded"
              aria-label="Odznacz wszystkie zaznaczone źródła"
            >
              Odznacz
            </button>
            {selectedIds.size > 50 && (
              <span className="text-xs text-red-600">Maksymalnie 50 źródeł na raz</span>
            )}
            {bulkDiscoveryMutation.isPending && selectedIds.size > 10 && (
              <span className="text-xs text-primary-600">
                Operacja może zająć do kilku minut dla dużej liczby źródeł
              </span>
            )}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie źródeł...
              </p>
            </div>
          ) : error ? (
            <ErrorState
              title="Błąd ładowania źródeł"
              message={(error as Error).message || "Nieznany błąd"}
              onRetry={handleRetry}
              retryLabel={isRetrying ? "Ładowanie..." : "Spróbuj ponownie"}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase w-10">
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={allNonManualSelected}
                        onChange={handleSelectAll}
                        data-testid="select-all-checkbox"
                        aria-label="Zaznacz wszystkie widoczne źródła"
                        aria-checked={someNonManualSelected ? "mixed" : allNonManualSelected}
                        className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                      />
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Nazwa
                    </th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase">
                      Typ
                    </th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase">
                      Status
                    </th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-neutral-500 uppercase">
                      robots.txt
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase cursor-pointer select-none hover:text-neutral-700"
                      onClick={() => handleSortClick("lastCheckedAt")}
                    >
                      <span className="inline-flex items-center">
                        Ostatnie sprawdzenie
                        {sortBy !== "lastCheckedAt" ? (
                          <svg className="w-4 h-4 ml-1 text-neutral-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        ) : sortOrder === "ASC" ? (
                          <svg className="w-4 h-4 ml-1 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 ml-1 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </span>
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Artykuły
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Projekt operatora
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Akcje
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {data?.sources?.map((source) => {
                    const isHighlighted = highlightId === source.id;
                    return (
                    <tr
                      key={source.id}
                      ref={isHighlighted ? highlightedRowRef : null}
                      className={`hover:bg-neutral-50 transition-colors duration-500 ${
                        isHighlighted ? "bg-yellow-100 ring-2 ring-yellow-400" : ""
                      }`}
                    >
                      <td className="px-4 py-4 text-center">
                        {source.sourceType !== MANUAL_SOURCE_TYPE && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(source.id)}
                            onChange={() => handleToggleSource(source.id)}
                            data-testid={`source-checkbox-${source.id}`}
                            aria-label={`Zaznacz źródło ${source.name}`}
                            className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                          />
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-neutral-900 text-left">
                          {source.name}
                        </div>
                        {source.url && (
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={source.url}
                            className="text-xs text-primary-600 hover:text-primary-800 hover:underline truncate max-w-[200px] block text-left"
                          >
                            {source.url}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <span className="text-sm text-neutral-900">
                          {sourceTypeLabels[source.sourceType] ||
                            source.sourceType}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <StatusBadge {...getStatusBadgeProps(source.status)} />
                        {source.errorCount > 0 && (
                          <span
                            className="ml-1 text-xs text-red-600"
                            title={source.lastError || "Błąd"}
                          >
                            ({source.errorCount} błędów)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <RobotsTxtStatusCell
                          source={source}
                          pendingCheck={robotsTxtCheckPending}
                          inlineOverrides={robotsTxtInlineStatus}
                          onCheck={handleCheckRobotsTxt}
                        />
                      </td>
                      <td className="px-4 py-4 text-sm text-neutral-500 text-left">
                        <div>{formatDate(source.lastCheckedAt)}</div>
                        {source.sourceType !== MANUAL_SOURCE_TYPE && source.checkFrequency && (
                          <div className="text-xs text-neutral-400 mt-1">
                            {describeCronPattern(source.checkFrequency) || source.checkFrequency}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-neutral-900">
                        {source.articlesFoundTotal}
                        {source.articlesFoundLastCheck > 0 && (
                          <span className="text-green-600 ml-1">
                            (+{source.articlesFoundLastCheck})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-500">
                        {source.project ? (
                          <>
                            <div className="text-sm font-medium text-left">
                              <Link
                                to={`/grants/projects/${source.project.id}`}
                                className="text-primary-600 hover:underline"
                              >
                                {source.project.name}
                              </Link>
                            </div>
                            {(source.project.operator ||
                              source.project.competition) && (
                              <div className="text-xs text-neutral-500 text-left">
                                {source.project.operator && (
                                  <Link
                                    to={`/grants/operators/${source.project.operator.id}`}
                                    className="hover:text-primary-600 hover:underline"
                                  >
                                    {source.project.operator.shortName ||
                                      source.project.operator.name}
                                  </Link>
                                )}
                                {source.project.operator && source.project.competition && (
                                  <span> · </span>
                                )}
                                {source.project.competition && (
                                  <Link
                                    to={`/grants/competitions/${source.project.competition.id}`}
                                    className="hover:text-primary-600 hover:underline"
                                  >
                                    {source.project.competition.code ||
                                      source.project.competition.name}
                                  </Link>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <Link
                          to={`/grants/sources/articles?sourceId=${source.id}`}
                          className="text-neutral-600 hover:text-neutral-800 mr-3"
                          title="Zobacz artykuły tego źródła"
                        >
                          Artykuły
                        </Link>
                        {/* Every source can miss an article its feed never lists,
                            so manual add is available regardless of source type. */}
                        <button
                          onClick={() => setAddArticleSource(source)}
                          className="text-primary-600 hover:text-primary-800 mr-3 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none rounded"
                          aria-label={`Dodaj artykuł do źródła ${source.name}`}
                          title="Dodaj artykuł ręcznie"
                        >
                          Dodaj artykuł
                        </button>
                        {source.sourceType === MANUAL_SOURCE_TYPE ? (
                          <button
                            onClick={() => setMarkCheckedSource(source)}
                            className="text-primary-700 hover:text-primary-800 mr-3 focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 focus:outline-none rounded"
                            aria-label={`Oznacz źródło ${source.name} jako sprawdzone`}
                            title="Oznacz jako sprawdzone"
                          >
                            Sprawdzone
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRunDiscovery(source)}
                            disabled={runningDiscoveryId === source.id || bulkDiscoveryMutation.isPending}
                            className={`mr-3 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none rounded ${
                              runningDiscoveryId === source.id || bulkDiscoveryMutation.isPending
                                ? "text-neutral-400 cursor-wait"
                                : "text-green-600 hover:text-green-800"
                            }`}
                            aria-label={`Uruchom discovery dla ${source.name}`}
                            title={bulkDiscoveryMutation.isPending ? "Trwa przetwarzanie bulk discovery" : "Uruchom discovery teraz"}
                          >
                            {runningDiscoveryId === source.id
                              ? "Działa..."
                              : "Uruchom"}
                          </button>
                        )}
                        <button
                          onClick={() => setEditingSourceId(source.id)}
                          className="text-primary-600 hover:text-primary-800 mr-3"
                          aria-label={`Edytuj źródło ${source.name}`}
                        >
                          Edytuj
                        </button>
                        <button
                          onClick={() => handleDelete(source)}
                          className="text-red-600 hover:text-red-800"
                          aria-label={`Usuń źródło ${source.name}`}
                        >
                          Usuń
                        </button>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>

              {(!data?.sources || data.sources.length === 0) && (
                <EmptyState
                  title={hasFiltersActive ? "Brak wyników" : "Brak źródeł"}
                  description={
                    hasFiltersActive
                      ? "Żadne źródło nie pasuje do wybranych filtrów."
                      : "Dodaj pierwsze źródło danych, aby rozpocząć zbieranie artykułów."
                  }
                  action={
                    hasFiltersActive ? (
                      <button
                        onClick={handleClearFilters}
                        className="mt-2 text-primary-600 hover:text-primary-800 font-medium"
                      >
                        Wyczyść filtry
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="mt-2 text-primary-600 hover:text-primary-800 font-medium"
                      >
                        Dodaj źródło
                      </button>
                    )
                  }
                  className="p-6"
                />
              )}
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <EditSourceModal
          isOpen={isAddModalOpen || !!editingSourceId}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingSourceId(null);
          }}
          sourceId={editingSourceId}
        />

        {/* Add Manual Article Modal */}
        <AddManualArticleModal
          isOpen={!!addArticleSource}
          onClose={() => setAddArticleSource(null)}
          source={addArticleSource}
        />

        {/* Mark Source Checked Modal */}
        <MarkSourceCheckedModal
          isOpen={!!markCheckedSource}
          onClose={() => setMarkCheckedSource(null)}
          sourceId={markCheckedSource?.id || ""}
          sourceName={markCheckedSource?.name || ""}
        />
      </div>
    </Layout>
  );
};

export default GrantsSources;
